/**
 * Zone Variance Calculation
 *
 * Calculates variance metrics that measure rider consistency within zones.
 * Identifies which aspects of riding show the most variation.
 */

import {
  type ZoneVarianceMetrics,
  type ZoneVarianceDetails,
  type TrackZoneType,
  type InconsistencyType,
  type NoiseToleranceConfig,
  type ZoneDetectionConfig,
  DEFAULT_NOISE_TOLERANCE,
} from './TrackZoneTypes'
import { type ZoneDataPoint } from './ZoneDetection'
import { type LapZoneMetrics, extractLapZoneMetrics } from './LapZoneMetrics'
import { average, standardDeviation } from './mathUtils'

// ============================================================================
// Consistency Score Configuration
// ============================================================================

/**
 * Configuration for consistency score calculation per zone type.
 *
 * IMPORTANT: Only include metrics that are visible to the user in insights.
 * This ensures transparency - users can see what affects their score.
 *
 * Weights are relative within each zone type. Higher = more impact on score.
 */
export const CONSISTENCY_CONFIG = {
  /**
   * Braking Zone Metrics (shown in insights):
   * - Entry speed: "Entry speed varies by X km/h"
   * - Brake point: "brake point moves Xm"
   * - Brake release: "Release point also varies by Xm" (when significant)
   */
  braking: {
    entrySpeed: 1.0,
    brakingPoint: 1.5, // Primary action - where you brake matters most
    brakeRelease: 1.0,
  },

  /**
   * Cornering Zone Metrics (shown in insights):
   * - Apex speed: "Min speed varies by X km/h"
   * - Apex location: "slowest point moves by Xm"
   */
  cornering: {
    apexSpeed: 1.5, // Primary metric - corner speed
    apexLocation: 1.0,
  },

  /**
   * Acceleration Zone Metrics (shown in insights):
   * - Exit speed: "Exit speed varies by X km/h"
   * - Throttle point: "throttle pickup varies by Xm"
   */
  acceleration: {
    exitSpeed: 1.0,
    throttlePoint: 1.5, // Primary action - where you get on throttle
  },
} as const

// ============================================================================
// Main Variance Calculation
// ============================================================================

/** Calculate variance metrics for a zone with detailed breakdown */
export function calculateVarianceMetrics(
  zoneData: ZoneDataPoint[][],
  config: ZoneDetectionConfig,
  zoneType?: TrackZoneType,
  zoneStartDistance: number = 0,
  noiseTolerance: NoiseToleranceConfig = DEFAULT_NOISE_TOLERANCE
): ZoneVarianceMetrics {
  const lapCount = zoneData.length
  const lapMetrics = zoneData.map((lapData) =>
    extractLapZoneMetrics(lapData, zoneStartDistance)
  )

  const details = calculateVarianceDetails(lapMetrics, zoneData, noiseTolerance)
  const primaryIssue = determinePrimaryIssue(details, zoneType)
  const primaryInconsistency = mapToLegacyInconsistency(primaryIssue)

  // Legacy metrics
  const peakLeans = zoneData.map((d) =>
    Math.max(...d.map((p) => Math.abs(p.leanAngle)))
  )
  const entryDistances = zoneData.map((d) => d[0]?.distance ?? 0)
  const exitDistances = zoneData.map((d) => d[d.length - 1]?.distance ?? 0)
  const minSpeeds = lapMetrics.map((m) => m.minSpeed)
  const speedCV =
    average(minSpeeds) > 0
      ? standardDeviation(minSpeeds) / average(minSpeeds)
      : 0

  // Continuous consistency score based on magnitude of variance
  const consistencyScore = calculateConsistencyScore(
    details,
    noiseTolerance,
    zoneType
  )

  return {
    speedCV,
    leanAngleStdDev: standardDeviation(peakLeans),
    entryDistanceStdDev: standardDeviation(entryDistances),
    exitDistanceStdDev: standardDeviation(exitDistances),
    consistencyScore,
    lapCount,
    primaryInconsistency,
    primaryIssue,
    details,
  }
}

// ============================================================================
// Variance Details
// ============================================================================

function calculateVarianceDetails(
  lapMetrics: LapZoneMetrics[],
  zoneData: ZoneDataPoint[][],
  noiseTolerance: NoiseToleranceConfig
): ZoneVarianceDetails {
  const zoneLength = calculateZoneLength(zoneData)

  return {
    entrySpeedVariance: calculateSpeedVariance(
      lapMetrics.map((m) => m.entrySpeed),
      noiseTolerance
    ),
    exitSpeedVariance: calculateSpeedVariance(
      lapMetrics.map((m) => m.exitSpeed),
      noiseTolerance
    ),
    apexSpeedVariance: calculateSpeedVariance(
      lapMetrics.map((m) => m.minSpeed),
      noiseTolerance
    ),
    apexLocationVariance: calculateApexLocationVariance(
      lapMetrics,
      zoneLength,
      noiseTolerance
    ),
    brakingPointVariance: calculateDistanceVariance(
      lapMetrics
        .map((m) => m.brakingPointDistance)
        .filter((d): d is number => d !== null),
      noiseTolerance
    ),
    brakeReleaseVariance: calculateDistanceVariance(
      lapMetrics
        .map((m) => m.brakeReleaseDistance)
        .filter((d): d is number => d !== null),
      noiseTolerance
    ),
    brakingIntensityVariance: calculateGForceVariance(
      lapMetrics.map((m) => m.peakBrakingG),
      noiseTolerance
    ),
    throttlePointVariance: calculateThrottlePointVariance(
      lapMetrics,
      zoneLength,
      noiseTolerance
    ),
    throttleIntensityVariance: calculateGForceVariance(
      lapMetrics.map((m) => m.peakThrottleG),
      noiseTolerance
    ),
    coastingVariance: calculateCoastingVariance(
      lapMetrics.map((m) => m.coastingDistance),
      noiseTolerance
    ),
  }
}

function calculateZoneLength(zoneData: ZoneDataPoint[][]): number {
  if (!zoneData[0] || zoneData[0].length === 0) return 0
  const first = zoneData[0][0]?.distance ?? 0
  const last = zoneData[0][zoneData[0].length - 1]?.distance ?? 0
  return last - first
}

function calculateSpeedVariance(
  speeds: number[],
  noiseTolerance: NoiseToleranceConfig
) {
  const range = Math.max(...speeds) - Math.min(...speeds)
  return {
    stdDev: standardDeviation(speeds),
    range,
    isSignificant: range > noiseTolerance.minSpeedVariance,
  }
}

function calculateDistanceVariance(
  distances: number[],
  noiseTolerance: NoiseToleranceConfig
) {
  if (distances.length < 2) {
    return { stdDev: 0, range: 0, isSignificant: false }
  }
  const range = Math.max(...distances) - Math.min(...distances)
  return {
    stdDev: standardDeviation(distances),
    range,
    isSignificant: range > noiseTolerance.minDistanceVariance,
  }
}

function calculateApexLocationVariance(
  lapMetrics: LapZoneMetrics[],
  zoneLength: number,
  noiseTolerance: NoiseToleranceConfig
) {
  const apexDistances = lapMetrics.map((m) => m.apexDistance)
  const range = Math.max(...apexDistances) - Math.min(...apexDistances)
  return {
    stdDev: standardDeviation(apexDistances),
    range,
    avgApexPercent:
      zoneLength > 0 ? (average(apexDistances) / zoneLength) * 100 : 50,
    isSignificant: range > noiseTolerance.minDistanceVariance,
  }
}

function calculateThrottlePointVariance(
  lapMetrics: LapZoneMetrics[],
  zoneLength: number,
  noiseTolerance: NoiseToleranceConfig
) {
  const throttlePoints = lapMetrics
    .map((m) => m.throttlePointDistance)
    .filter((d): d is number => d !== null)
  if (throttlePoints.length < 2) {
    return { stdDev: 0, range: 0, avgThrottlePercent: 50, isSignificant: false }
  }
  const range = Math.max(...throttlePoints) - Math.min(...throttlePoints)
  return {
    stdDev: standardDeviation(throttlePoints),
    range,
    avgThrottlePercent:
      zoneLength > 0 ? (average(throttlePoints) / zoneLength) * 100 : 50,
    isSignificant: range > noiseTolerance.minDistanceVariance,
  }
}

function calculateGForceVariance(
  gForces: number[],
  noiseTolerance: NoiseToleranceConfig
) {
  const range = Math.max(...gForces) - Math.min(...gForces)
  return {
    stdDev: standardDeviation(gForces),
    range,
    avgPeakG: average(gForces),
    isSignificant: range > noiseTolerance.minGVariance,
  }
}

function calculateCoastingVariance(
  coastingDistances: number[],
  noiseTolerance: NoiseToleranceConfig
) {
  const range = Math.max(...coastingDistances) - Math.min(...coastingDistances)
  return {
    distanceStdDev: standardDeviation(coastingDistances),
    distanceRange: range,
    avgDistance: average(coastingDistances),
    isSignificant: range > noiseTolerance.minDistanceVariance,
  }
}

/**
 * Calculate consistency score based on magnitude of variance relative to thresholds.
 *
 * Uses CONSISTENCY_CONFIG to determine which metrics are included and their weights.
 * Only metrics that are visible to the user in insights are included.
 *
 * Score interpretation:
 * - 100: Perfect consistency (all variance at zero)
 * - 85+: Excellent consistency (all variance below thresholds)
 * - 60-84: Good consistency (some variance above thresholds)
 * - 40-59: Moderate inconsistency (needs attention)
 * - 20-39: Significant inconsistency (major focus area)
 * - 0-19: High inconsistency (critical)
 */
function calculateConsistencyScore(
  details: ZoneVarianceDetails,
  noiseTolerance: NoiseToleranceConfig,
  zoneType?: TrackZoneType
): number {
  const scores: { score: number; weight: number }[] = []

  if (zoneType === 'braking') {
    const config = CONSISTENCY_CONFIG.braking
    scores.push(
      {
        score: calculateMetricConsistency(
          details.entrySpeedVariance.range,
          noiseTolerance.minSpeedVariance
        ),
        weight: config.entrySpeed,
      },
      {
        score: calculateMetricConsistency(
          details.brakingPointVariance.range,
          noiseTolerance.minDistanceVariance
        ),
        weight: config.brakingPoint,
      },
      {
        score: calculateMetricConsistency(
          details.brakeReleaseVariance.range,
          noiseTolerance.minDistanceVariance
        ),
        weight: config.brakeRelease,
      }
    )
  } else if (zoneType === 'cornering') {
    const config = CONSISTENCY_CONFIG.cornering
    scores.push(
      {
        score: calculateMetricConsistency(
          details.apexSpeedVariance.range,
          noiseTolerance.minSpeedVariance
        ),
        weight: config.apexSpeed,
      },
      {
        score: calculateMetricConsistency(
          details.apexLocationVariance.range,
          noiseTolerance.minDistanceVariance
        ),
        weight: config.apexLocation,
      }
    )
  } else if (zoneType === 'acceleration') {
    const config = CONSISTENCY_CONFIG.acceleration
    scores.push(
      {
        score: calculateMetricConsistency(
          details.exitSpeedVariance.range,
          noiseTolerance.minSpeedVariance
        ),
        weight: config.exitSpeed,
      },
      {
        score: calculateMetricConsistency(
          details.throttlePointVariance.range,
          noiseTolerance.minDistanceVariance
        ),
        weight: config.throttlePoint,
      }
    )
  }

  // Fallback for unknown zone types
  if (scores.length === 0) {
    scores.push(
      {
        score: calculateMetricConsistency(
          details.entrySpeedVariance.range,
          noiseTolerance.minSpeedVariance
        ),
        weight: 1,
      },
      {
        score: calculateMetricConsistency(
          details.exitSpeedVariance.range,
          noiseTolerance.minSpeedVariance
        ),
        weight: 1,
      }
    )
  }

  // Calculate weighted average
  const totalWeight = scores.reduce((sum, s) => sum + s.weight, 0)
  const weightedSum = scores.reduce((sum, s) => sum + s.score * s.weight, 0)

  return Math.round(weightedSum / totalWeight)
}

/**
 * Calculate consistency score for a single metric (0-100).
 *
 * Uses a continuous decay that starts from zero variance:
 * - 0 variance: 100 (perfect)
 * - 50% of threshold: ~93
 * - At threshold: ~85
 * - 2x threshold: ~55
 * - 3x threshold: ~30
 * - 5x+ threshold: 0
 *
 * This ensures even "within tolerance" variance shows some impact,
 * while still heavily penalizing significant variance.
 */
function calculateMetricConsistency(
  varianceRange: number,
  threshold: number
): number {
  if (varianceRange <= 0) return 100

  // Ratio of variance to threshold
  const ratio = varianceRange / threshold

  // Two-phase decay:
  // Below threshold: gentle linear decay (0 to 1 → 100 to 85)
  // Above threshold: steeper logarithmic decay
  if (ratio <= 1) {
    // Linear decay: lose 15 points max when at threshold
    return Math.round(100 - ratio * 15)
  }

  // Above threshold: logarithmic decay from 85
  // log2(1) = 0 → 85, log2(2) = 1 → 55, log2(4) = 2 → 25, log2(6) = 2.58 → 0
  const logRatio = Math.log2(ratio)
  const score = 85 - logRatio * 30

  return Math.max(0, Math.min(100, Math.round(score)))
}

/** @deprecated Use calculateConsistencyScore instead - kept for backward compatibility in tests */
function countSignificantIssues(
  details: ZoneVarianceDetails,
  zoneType?: TrackZoneType
): number {
  // Core issues that matter for all zones
  const coreIssues = [
    details.entrySpeedVariance.isSignificant,
    details.exitSpeedVariance.isSignificant,
  ]

  // Zone-type specific issues
  const zoneSpecificIssues: boolean[] = []

  if (zoneType === 'braking') {
    zoneSpecificIssues.push(
      details.brakingPointVariance.isSignificant,
      details.brakingIntensityVariance.isSignificant,
      details.brakeReleaseVariance.isSignificant
    )
  } else if (zoneType === 'cornering') {
    zoneSpecificIssues.push(
      details.apexSpeedVariance.isSignificant,
      details.apexLocationVariance.isSignificant
    )
  } else if (zoneType === 'acceleration') {
    zoneSpecificIssues.push(
      details.throttlePointVariance.isSignificant,
      details.throttleIntensityVariance.isSignificant
    )
  }

  return [...coreIssues, ...zoneSpecificIssues].filter(Boolean).length
}

// ============================================================================
// Issue Prioritization
// ============================================================================

function determinePrimaryIssue(
  details: ZoneVarianceDetails,
  zoneType?: TrackZoneType
): InconsistencyType {
  const issues: { type: InconsistencyType; priority: number }[] = []

  if (details.entrySpeedVariance.isSignificant) {
    issues.push({
      type: 'entry-speed',
      priority: zoneType === 'braking' ? 100 : 60,
    })
  }
  if (details.brakingPointVariance.isSignificant) {
    issues.push({
      type: 'braking-point',
      priority: zoneType === 'braking' ? 95 : 40,
    })
  }
  if (details.brakingIntensityVariance.isSignificant) {
    issues.push({
      type: 'braking-intensity',
      priority: zoneType === 'braking' ? 80 : 30,
    })
  }
  if (details.apexLocationVariance.isSignificant) {
    issues.push({
      type: 'apex-location',
      priority: zoneType === 'cornering' ? 90 : 70,
    })
  }
  if (details.apexSpeedVariance.isSignificant) {
    issues.push({
      type: 'apex-speed',
      priority: zoneType === 'cornering' ? 85 : 65,
    })
  }
  if (details.throttlePointVariance.isSignificant) {
    issues.push({
      type: 'throttle-point',
      priority:
        zoneType === 'acceleration' || zoneType === 'cornering' ? 85 : 50,
    })
  }
  if (details.throttleIntensityVariance.isSignificant) {
    issues.push({
      type: 'throttle-intensity',
      priority: zoneType === 'acceleration' ? 80 : 40,
    })
  }
  if (details.coastingVariance.isSignificant) {
    issues.push({ type: 'coasting-duration', priority: 55 })
  }
  if (details.exitSpeedVariance.isSignificant) {
    issues.push({
      type: 'exit-speed',
      priority: zoneType === 'acceleration' ? 90 : 50,
    })
  }

  if (issues.length === 0) return null

  issues.sort((a, b) => b.priority - a.priority)
  return issues[0].type
}

function mapToLegacyInconsistency(
  primaryIssue: InconsistencyType
): 'speed' | 'line' | 'timing' | 'technique' | null {
  if (!primaryIssue) return null

  const speedIssues: InconsistencyType[] = [
    'entry-speed',
    'exit-speed',
    'apex-speed',
  ]
  const timingIssues: InconsistencyType[] = ['braking-point', 'throttle-point']
  const techniqueIssues: InconsistencyType[] = [
    'braking-intensity',
    'throttle-intensity',
    'coasting-duration',
  ]

  if (speedIssues.includes(primaryIssue)) return 'speed'
  if (primaryIssue === 'apex-location') return 'line'
  if (timingIssues.includes(primaryIssue)) return 'timing'
  if (techniqueIssues.includes(primaryIssue)) return 'technique'

  return null
}
